import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { Router, RouterLink, UrlTree } from '@angular/router';
import { IrdFormatPipe, IrdFormatService } from '@ird/ng-core';
import { IconComponent } from '@ird/ng-base';
import { IrdIconRef, IrdResolvedProperty, IrdValueFormatter } from '@ird/shared-base';

/**
 * What the template draws. Anything that cannot be drawn in its declared mode is `text`.
 * A type alias cannot be a class member, so it is the one declaration of this file outside the class.
 */
type PropertyValueView =
  | { readonly kind: 'text' }
  | { readonly kind: 'color'; readonly color: string }
  | { readonly kind: 'icon'; readonly src: string }
  | { readonly kind: 'font-icon'; readonly name: string; readonly fontSet: string }
  | { readonly kind: 'svg-icon'; readonly name: string; readonly namespace: string | undefined }
  | { readonly kind: 'link'; readonly url: string; readonly route: UrlTree | null }
  | { readonly kind: 'json'; readonly json: string };

/**
 * Renders one value of one property, in the property's render mode: the markup around the string
 * `irdFormat` produces. Every branch shows that same string where it shows text, so a tooltip,
 * the clipboard and an export read what this shows.
 *
 * A value that cannot be drawn in its mode — absent, the wrong shape, an address that is neither a
 * path in the application nor a web address, an image that fails to load, or a declared `icon`/`url`
 * that throws — reads as text instead, and a throw is logged rather than propagated.
 */
@Component({
  selector: 'shared-property-value',
  templateUrl: './property-value.component.html',
  styleUrls: ['./property-value.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, IrdFormatPipe, RouterLink],
})
export class PropertyValueComponent<T = unknown> {
  private static readonly TEXT: PropertyValueView = { kind: 'text' };
  private static readonly JSON_INDENT = 2;
  /** The font `spx-icon` draws a ligature in unless told otherwise. */
  private static readonly DEFAULT_FONT_SET = 'material-icons';
  /** A path inside the application: one leading `/`, not `//host` or `/\host`, which a browser reads as another site. */
  private static readonly APPLICATION_PATH = /^\/(?![/\\])/;
  private static readonly WEB_URL = /^https?:\/\//i;
  private static readonly EXTERNAL_LINK = /^(https?:\/\/|mailto:)/i;

  /** A resolved property keeps its identity for the application's lifetime, which the pure pipe relies on. */
  public readonly property = input.required<IrdResolvedProperty<T>>();
  public readonly value = input<unknown>();

  protected readonly translationsVersion = inject(IrdFormatService).translationsVersion;
  /** The image source that failed to load; a new source is tried again. */
  protected readonly brokenIcon = signal<string | null>(null);
  private readonly router = inject(Router);

  protected readonly view = computed<PropertyValueView>(() => {
    const property = this.property();
    const value = this.value();
    if (!IrdValueFormatter.hasDisplayValue(value)) {
      return PropertyValueComponent.TEXT;
    }
    try {
      return this.toView(property, value);
    } catch (error) {
      console.error(`shared-property-value: '${property.key}' could not be rendered as ${property.renderAs.kind}`, error);
      return PropertyValueComponent.TEXT;
    }
  });

  private toView(property: IrdResolvedProperty<T>, value: unknown): PropertyValueView {
    const { TEXT, APPLICATION_PATH, WEB_URL, EXTERNAL_LINK, JSON_INDENT } = PropertyValueComponent;
    const mode = property.renderAs;
    switch (mode.kind) {
      case 'text':
        return TEXT;
      case 'color':
        return typeof value === 'string' && PropertyValueComponent.isColor(value) ? { kind: 'color', color: value } : TEXT;
      case 'icon': {
        let icon: IrdIconRef | null = null;
        if (mode.icon) {
          icon = mode.icon(value);
        } else if (typeof value === 'string') {
          icon = { url: value };
        }
        if (icon && 'font' in icon && icon.font) {
          return { kind: 'font-icon', name: icon.font, fontSet: icon.fontSet ?? PropertyValueComponent.DEFAULT_FONT_SET };
        }
        if (icon && 'svg' in icon && icon.svg) {
          return { kind: 'svg-icon', name: icon.svg, namespace: icon.namespace };
        }
        const src = icon && 'url' in icon ? icon.url : undefined;
        return src && (APPLICATION_PATH.test(src) || WEB_URL.test(src)) ? { kind: 'icon', src } : TEXT;
      }
      case 'link': {
        const url = mode.url(value);
        if (url && APPLICATION_PATH.test(url)) {
          // A string routerLink is a path command and would encode `?` and `#`; a parsed tree keeps them.
          return { kind: 'link', url, route: this.router.parseUrl(url) };
        }
        return url && EXTERNAL_LINK.test(url) ? { kind: 'link', url, route: null } : TEXT;
      }
      case 'json': {
        const parsed = typeof value === 'string' ? PropertyValueComponent.parseJson(value) : value;
        const json = JSON.stringify(parsed, null, JSON_INDENT);
        return json === undefined ? TEXT : { kind: 'json', json };
      }
    }
  }

  /** A colour the browser can paint. Without a CSS engine to ask (a test DOM), every string passes. */
  private static isColor(value: string): boolean {
    return typeof CSS === 'undefined' || typeof CSS.supports !== 'function' || CSS.supports('color', value);
  }

  /** A service may deliver JSON as its text; that text is indented as the structure, not quoted as a string. */
  private static parseJson(text: string): unknown {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
}
